/**
 * Centralized matchers for session-related 401 error responses.
 *
 * The backend communicates session/context-token problems through several
 * potentially-populated fields on the 401 response body:
 *
 *   1. `code` — machine-readable, e.g. `SESSION_DEACTIVATED`,
 *      `CONTEXT_TOKEN_MISSING`, `INVALID_CONTEXT_TOKEN`, `INVALID_SPONSOR_CONTEXT`.
 *      Note the inconsistent naming: `INVALID_CONTEXT_TOKEN` does NOT share the
 *      `CONTEXT_TOKEN_*` prefix used by the other context-token codes, so a
 *      `startsWith` check misses it.
 *   2. `resolution` — backend's explicit hint to the client about how to
 *      recover. Values: `RESTORE_TAB_SESSION` (treat as displacement) or
 *      `REFRESH_CONTEXT_TOKEN` (try silent refresh). When set, this is
 *      authoritative regardless of the `code` field.
 *   3. `reason` — secondary machine-readable detail. The channel-gone case is
 *      signalled via `reason === "SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT"` (often
 *      alongside `code === "INVALID_SPONSOR_CONTEXT"`).
 *   4. `error` — human-readable string. Some legacy responses emit only this,
 *      with no code/resolution; a small allow-list covers those cases.
 *
 * Classification precedence on any 401 (see `classifySessionError`):
 *   Step 1 — `sponsor_inactive` (channel/sponsor deleted): TERMINAL. There is
 *            no session to resume and no token that will ever validate, because
 *            the sponsor no longer exists. Wins over everything else so the
 *            channel-gone case never falls through into the takeover dialog
 *            (whose "Resume Here" → /context/reactivate would re-mint against
 *            the dead sponsor and 401 again — the reported loop).
 *   Step 2 — `session_deactivated` (real tab/device takeover): show the
 *            takeover dialog with "Resume Here".
 *   Step 3 — `context_token` (recoverable token problem): silent refresh.
 *
 * These matchers are the single source of truth used by both the axios
 * response error interceptor (auto-retry path) and `addResponseTransform`
 * (callback dispatch / fallback path) in `api.ts`.
 */

/**
 * Subset of the 401 response body fields we inspect. Kept loose because the
 * backend may add new fields and we do not want to break on extra keys.
 */
export type SessionErrorBody = {
  code?: string
  resolution?: string
  reason?: string
  error?: string
}

/**
 * Codes that indicate the active sponsor/channel itself is gone (deleted or
 * deactivated in the relevant environment). This is a TERMINAL state — not a
 * displacement and not a recoverable token problem. The only correct action is
 * to clear local session state and send the user back to channel selection /
 * login. Never offer "Resume Here" and never silently reactivate/refresh.
 */
const SPONSOR_INACTIVE_CODES = new Set<string>(["INVALID_SPONSOR_CONTEXT"])

/**
 * `reason` values that map to the terminal sponsor-inactive flow. The backend
 * may send this with or without the matching `code`.
 */
const SPONSOR_INACTIVE_REASONS = new Set<string>(["SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT"])

/**
 * Codes that indicate the active web session was displaced (taken over by
 * another tab/device or otherwise invalidated). Recovery requires either an
 * explicit user choice ("Resume Here" → POST /context/reactivate) or a fresh login.
 */
const SESSION_DEACTIVATED_CODES = new Set<string>(["SESSION_DEACTIVATED"])

/**
 * Resolution hints from the backend that map to the displacement flow.
 */
const RESTORE_TAB_SESSION_RESOLUTIONS = new Set<string>(["RESTORE_TAB_SESSION"])

/**
 * Codes that indicate the context token itself is the problem (missing,
 * malformed, expired). These are recoverable via silent POST /context/refresh —
 * they do NOT indicate displacement.
 *
 * Includes both naming conventions the backend uses today
 * (`CONTEXT_TOKEN_INVALID` and the un-prefixed `INVALID_CONTEXT_TOKEN`). The
 * separate prefix check below provides forward-compat for any new
 * `CONTEXT_TOKEN_*` codes the backend may add.
 */
const CONTEXT_TOKEN_ERROR_CODES = new Set<string>([
  "CONTEXT_TOKEN_MISSING",
  "CONTEXT_TOKEN_REQUIRED",
  "CONTEXT_TOKEN_EXPIRED",
  "CONTEXT_TOKEN_INVALID",
  "INVALID_CONTEXT_TOKEN",
])

/**
 * Resolution hints from the backend that map to the silent-refresh flow.
 */
const REFRESH_CONTEXT_TOKEN_RESOLUTIONS = new Set<string>(["REFRESH_CONTEXT_TOKEN"])

/**
 * Legacy error-message strings that some endpoints emit instead of a `code`.
 * Match the reference's allow-list verbatim — adding new strings should be
 * coordinated with the backend.
 */
const CONTEXT_TOKEN_ERROR_MESSAGES = new Set<string>([
  "Context token header missing",
  "Context token is required",
  "Invalid or expired context token",
])

/**
 * True when the 401 response indicates the sponsor/channel is no longer active
 * (deleted out from under the user). TERMINAL — not recoverable. Matched via
 * `code === "INVALID_SPONSOR_CONTEXT"` OR `reason === "SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT"`.
 */
export function isSponsorInactiveError(
  status: number | undefined,
  body: SessionErrorBody | undefined,
): boolean {
  if (status !== 401 || !body) return false
  if (body.code && SPONSOR_INACTIVE_CODES.has(body.code)) return true
  if (body.reason && SPONSOR_INACTIVE_REASONS.has(body.reason)) return true
  return false
}

/**
 * True when the 401 response indicates the underlying session was displaced
 * (matches `SESSION_DEACTIVATED` flow).
 */
export function isSessionDeactivatedError(
  status: number | undefined,
  body: SessionErrorBody | undefined,
): boolean {
  if (status !== 401 || !body) return false
  if (body.code && SESSION_DEACTIVATED_CODES.has(body.code)) return true
  if (body.resolution && RESTORE_TAB_SESSION_RESOLUTIONS.has(body.resolution)) return true
  return false
}

/**
 * True when the 401 response indicates a recoverable context-token problem
 * (matches the silent POST /context/refresh flow).
 *
 * SESSION_DEACTIVATED takes precedence: callers checking both should test
 * `isSessionDeactivatedError` first.
 */
export function isContextTokenError(
  status: number | undefined,
  body: SessionErrorBody | undefined,
): boolean {
  if (status !== 401 || !body) return false
  if (body.code) {
    if (CONTEXT_TOKEN_ERROR_CODES.has(body.code)) return true
    // Forward-compat: any new `CONTEXT_TOKEN_*` code the backend introduces
    // (e.g. CONTEXT_TOKEN_REVOKED) is treated as recoverable by default.
    if (body.code.startsWith("CONTEXT_TOKEN_")) return true
  }
  if (body.resolution && REFRESH_CONTEXT_TOKEN_RESOLUTIONS.has(body.resolution)) return true
  if (body.error && CONTEXT_TOKEN_ERROR_MESSAGES.has(body.error)) return true
  return false
}

/**
 * Convenience: classify a 401 response into its recovery branch.
 * Returns one of `"sponsor_inactive" | "session_deactivated" | "context_token" | "other"`.
 *
 * Precedence (see the file header for the full rationale):
 *   1. sponsor_inactive (channel gone)  — TERMINAL, wins over everything.
 *   2. session_deactivated (takeover)   — wins over context_token.
 *   3. context_token (token problem)    — silent refresh.
 *   4. other                            — generic auth failure.
 *
 * sponsor_inactive must be checked FIRST so the channel-gone case never falls
 * through into the takeover/refresh paths (the bug this guards against).
 */
export function classifySessionError(
  status: number | undefined,
  body: SessionErrorBody | undefined,
): "sponsor_inactive" | "session_deactivated" | "context_token" | "other" {
  if (isSponsorInactiveError(status, body)) return "sponsor_inactive"
  if (isSessionDeactivatedError(status, body)) return "session_deactivated"
  if (isContextTokenError(status, body)) return "context_token"
  return "other"
}
